import { Typography } from '@mui/material';
import { Column } from '../../../../components/GenericTable/GenericTable';
import { CLIENT_USER_ROLE, CLIENT_USER_STATUS } from '@api/types/client-user-management';
import { PVX_PARTNERS_TYPE } from '@api/types/pvxPartners';
import { formatDate } from '@utils/common';
import { PvxPartnerTableRow } from '../hooks/usePvxPartnersTable';
import {
  CLIENT_ROLE_DISPLAY_LABELS,
  CLIENT_ROLE_FILTER_OPTIONS,
  CLIENT_STATUS_DISPLAY_LABELS,
  CLIENT_STATUS_FILTER_OPTIONS,
  PARTNER_TYPE_DISPLAY_LABELS,
  PARTNER_TYPE_FILTER_OPTIONS,
} from '../constants';
import {
  cellTextSx,
  DashboardCell,
  renderActionsCell,
  renderCustomHeader,
  renderFilterableHeader,
  renderSortableHeader,
} from './tableColumnHelpers';

const formatRoleLabel = (role: string) => {
  if (role === '-') return role;
  return CLIENT_ROLE_DISPLAY_LABELS[role as CLIENT_USER_ROLE] ?? role;
};

const formatStatusLabel = (status: PvxPartnerTableRow['status']) => {
  if (status === '-') return status;
  return CLIENT_STATUS_DISPLAY_LABELS[status as CLIENT_USER_STATUS] ?? status;
};

const formatTypeLabel = (type: PVX_PARTNERS_TYPE) => PARTNER_TYPE_DISPLAY_LABELS[type] ?? type;

type PvxPartnersTableColumnOptions = {
  onActionClick: (event: React.MouseEvent<HTMLElement>, row: PvxPartnerTableRow) => void;
  onEditCompany: (row: PvxPartnerTableRow) => void;
  sort: string;
  sortOrder: 'ASC' | 'DESC';
  onSort: (sortKey: string) => void;
  roleFilter: string;
  onRoleFilterChange: (filter: string) => void;
  statusFilter: string;
  onStatusFilterChange: (filter: string) => void;
  typeFilter: string;
  onTypeFilterChange: (filter: string) => void;
};

export const getPvxPartnersTableColumns = ({
  onActionClick,
  onEditCompany,
  sort,
  sortOrder,
  onSort,
  roleFilter,
  onRoleFilterChange,
  statusFilter,
  onStatusFilterChange,
  typeFilter,
  onTypeFilterChange,
}: PvxPartnersTableColumnOptions): Column<PvxPartnerTableRow>[] => [
  {
    key: 'company',
    label: 'Company',
    renderHeader: renderCustomHeader('Company'),
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, minWidth: '160px', whiteSpace: 'nowrap' }}>
        {row.company || '-'}
      </Typography>
    ),
  },
  {
    key: 'partnerType',
    label: 'Type',
    renderHeader: renderFilterableHeader(
      'Type',
      PARTNER_TYPE_FILTER_OPTIONS,
      typeFilter,
      onTypeFilterChange,
    ),
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, minWidth: '140px' }}>
        {row.rowType === 'company' ? formatTypeLabel(row.partnerType) : '-'}
      </Typography>
    ),
  },
  {
    key: 'user',
    label: 'User',
    renderHeader: renderCustomHeader('User'),
    render: (_value, row) => <Typography sx={cellTextSx}>{row.user || '-'}</Typography>,
  },
  {
    key: 'role',
    label: 'Role',
    renderHeader: renderFilterableHeader(
      'Role',
      CLIENT_ROLE_FILTER_OPTIONS,
      roleFilter,
      onRoleFilterChange,
    ),
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, minWidth: '100px' }}>
        {row.role ? formatRoleLabel(row.role) : '-'}
      </Typography>
    ),
  },
  {
    key: 'dashboards',
    label: 'Dashboards',
    renderHeader: renderCustomHeader('Dashboards'),
    render: (_value, row) => <DashboardCell row={row} />,
  },
  {
    key: 'joiningDate',
    label: 'Joining Date',
    renderHeader: renderSortableHeader('Joining Date', 'joiningDate', sort, sortOrder, onSort),
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, minWidth: '120px' }}>
        {row.joiningDate && row.joiningDate !== '-' ? formatDate(row.joiningDate) : '-'}
      </Typography>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    renderHeader: renderFilterableHeader(
      'Status',
      CLIENT_STATUS_FILTER_OPTIONS,
      statusFilter,
      onStatusFilterChange,
    ),
    render: (_value, row) => (
      <Typography sx={{ ...cellTextSx, minWidth: '100px' }}>
        {row.status ? formatStatusLabel(row.status) : '-'}
      </Typography>
    ),
  },
  {
    key: 'actions' as keyof PvxPartnerTableRow,
    label: 'Actions',
    renderHeader: renderCustomHeader('Actions'),
    render: (_value, row) => renderActionsCell(row, onEditCompany, onActionClick),
  },
];
